import React from 'react';
import { FieldValues, Path } from 'react-hook-form';
import { Form, FormRef, FormProps } from './Form';
import { InputProps, Input, InputRef } from './Input';
import { PasswordInputProps, PasswordInput, PasswordInputRef } from './PasswordInput';
import { SelectRef, SelectProps, Select } from './Select';
import { DataGridCtor } from 'core/ui/controls/DataGrid';
import { LegacyDataGrid } from './LegacyDataGrid';
import { EditableValueListCtor } from 'core/ui/controls/EditableValueList';
import { LegacyEditableValueList } from './LegacyEditableValueList';
import { DatePickerCtor } from 'core/ui/controls/DatePicker';
import { LegacyDatePicker } from './LegacyDatePicker';
import { IntInputProps, IntInputRef, IntInput } from './IntInput';
import { DecimalInput, DecimalInputProps, DecimalInputRef } from './DecimalInput';
import { DateInputProps, DateInput, DateInputRef } from './DateInput';
import { TextArea, TextAreaProps, TextAreaRef } from './TextArea';
import { ComboBox, ComboBoxProps, ComboBoxRef } from './ComboBox';
import { RadioGroup, RadioGroupProps, RadioGroupRef } from './RadioGroup';
import { CheckBox, CheckBoxProps, CheckBoxRef } from './CheckBox';
import { CheckBoxMenu, CheckBoxMenuProps, CheckBoxMenuRef } from './CheckBoxMenu';

export function legacyControlsFor<T>() {

	const dataGrid: DataGridCtor<T> = LegacyDataGrid;
	const editableValueList: EditableValueListCtor<T> = LegacyEditableValueList;
	const datePicker: DatePickerCtor<T> = LegacyDatePicker;

	return {
		DataGrid: dataGrid,
		EditableValueList: editableValueList,
		DatePicker: datePicker
	};
}

export function controlsFor<T extends FieldValues>() {

	const select = <TOption extends Path<T>,>(props: SelectProps<T, TOption> & { ref?: React.Ref<SelectRef> }) => {
		const { ref, ...selectProps } = props;
		return <Select ref={ref} {...selectProps} />;
	};

	const input = <TOption extends Path<T>,>(props: InputProps<T, TOption> & { ref?: React.Ref<InputRef> }) => {
		const { ref, ...inputProps } = props;
		return <Input ref={ref} {...inputProps} />;
	};

	const intInput = <TOption extends Path<T>,>(props: IntInputProps<T, TOption> & { ref?: React.Ref<IntInputRef> }) => {
		const { ref, ...inputProps } = props;
		return <IntInput ref={ref} {...inputProps} />;
	};

	const decimalInput = <TOption extends Path<T>,>(props: DecimalInputProps<T, TOption> & { ref?: React.Ref<DecimalInputRef> }) => {
		const { ref, ...inputProps } = props;
		return <DecimalInput ref={ref} {...inputProps} />;
	};

	const dateInput = <TOption extends Path<T>,>(props: DateInputProps<T, TOption> & { ref?: React.Ref<DateInputRef> }) => {
		const { ref, ...inputProps } = props;
		return <DateInput ref={ref} {...inputProps} />;
	};

	const passwordInput = <TOption extends Path<T>,>(props: PasswordInputProps<T, TOption> & { ref?: React.Ref<PasswordInputRef> }) => {
		const { ref, ...inputProps } = props;
		return <PasswordInput ref={ref} {...inputProps} />;
	};

	const textArea = <TOption extends Path<T>,>(props: TextAreaProps<T, TOption> & { ref?: React.Ref<TextAreaRef> }) => {
		const { ref, ...textareaProps } = props;
		return <TextArea ref={ref} {...textareaProps} />;
	};

	const comboBox = <TOption extends Path<T>,>(props: ComboBoxProps<T, TOption> & { ref?: React.Ref<ComboBoxRef> }) => {
		const { ref, ...comboBoxProps } = props;
		return <ComboBox ref={ref} {...comboBoxProps} />;
	};

	const radioGroup = <TOption extends Path<T>,>(props: RadioGroupProps<T, TOption> & { ref?: React.Ref<RadioGroupRef> }) => {
		const { ref, ...radioGroupProps } = props;
		return <RadioGroup ref={ref} {...radioGroupProps} />;
	};

	const checkBox = <TOption extends Path<T>,>(props: CheckBoxProps<T, TOption> & { ref?: React.Ref<CheckBoxRef> }) => {
		const { ref, ...checkBoxProps } = props;
		return <CheckBox ref={ref} {...checkBoxProps} />;
	};

	const checkBoxMenu = (props: CheckBoxMenuProps & { ref?: React.Ref<CheckBoxMenuRef> }) => {
		const { ref, ...checkBoxMenuProps } = props;
		return <CheckBoxMenu ref={ref} {...checkBoxMenuProps} />;
	};

	return {
		Form: (props: FormProps<T>) => <Form {...props} />,
		Input: input,
		IntInput: intInput,
		PasswordInput: passwordInput,
		DecimalInput: decimalInput,
		DateInput: dateInput,
		Select: select,
		TextArea: textArea,
		ComboBox: comboBox,
		RadioGroup: radioGroup,
		CheckBox: checkBox,
		CheckBoxMenu: checkBoxMenu
	};
}

// OJO: usar este método cuando lo usemos desde FunctionalComponents, si usamos el controlsFor, se crean nuevas referencias y renderiza nuevos Forms
export function useControlsFor<T extends FieldValues>() {
	const controls = React.useMemo(() => controlsFor<T>(), []);
	return controls;
}

export type {
	FormRef,
	InputRef,
	SelectRef
};